import { zodResolver } from '@hookform/resolvers/zod'
import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { LanguageSelect } from '@/components/ui/LanguageSelect'
import { Logo } from '@/components/ui/Logo'
import { signInFailure, useSignIn } from '@/features/auth/api/useSignIn'
import { useSession } from '@/features/auth/api/useSession'
import { LANGUAGE_STORAGE_KEY, type Language } from '@/i18n/config'
import { useLanguage } from '@/i18n/useLanguage'
import { duration, ease } from '@/lib/motion'

/* Sign-in (FR-57, epics Story 1.2).
 *
 * This is the one screen the design rules exempt from the no-decoration rule — the
 * brand pattern is banned from every dense operational surface and permitted
 * "possibly" only here. The screen leans into that exemption: an animated
 * brand-tinted aurora behind the card, a pointer-tracked 3D tilt on the card
 * itself, and a staggered entrance. All of it is decoration layered AROUND the
 * same controls, tokens and voice as the rest of the app — and all of it
 * collapses to a plain fade under prefers-reduced-motion.
 *
 * TWO THINGS HERE ARE DELIBERATE AND SHOULD NOT BE "FIXED":
 *
 * 1. THE FAILURE MESSAGE NEVER NAMES WHICH FIELD WAS WRONG. Story 1.2 requires
 *    the server's body AND timing to be identical whether or not the email
 *    exists, so the endpoint cannot be used to discover who has an account.
 *    A friendlier "we don't know that email" would give away from the client
 *    precisely what the server is paying a timing cost to conceal.
 *
 * 2. THE ERROR SITS ABOVE THE FORM, NOT BESIDE A FIELD. The system puts
 *    field errors beside their field and record errors above the record; a
 *    refusal that deliberately does not attribute itself to one of the two
 *    fields is a record error by that rule. Client-side SHAPE errors — an
 *    empty box, a malformed address — still sit beside their field, because
 *    those the client does know.
 *
 * There is no "forgotten password" link: v1 has no self-serve reset, so the
 * copy names the Super-admin instead of offering a link that goes nowhere
 * (FR-57). There is no "remember me": session lifetime is fixed server-side at
 * eight hours idle and seven days absolute, and a checkbox implying otherwise
 * would be a lie the client cannot honour.
 */

/** Where a signed-in user is sent when they hit `/login` with no history to
 *  return to. Role-specific homes resolve inside the shell (Story 1.4), so the
 *  route is the same for everyone. */
const DEFAULT_LANDING = '/'

const credentialsSchema = z.object({
  /* Trimmed before validation: a pasted address or a phone keyboard's trailing
     space is a typo the user cannot see, and refusing it teaches nothing. */
  email: z.string().trim().min(1, 'emailRequired').email('emailInvalid'),
  // Only presence is checked. The server owns the password policy, and a
  // client-side length rule here would lock out any account whose password
  // predates it.
  password: z.string().min(1, 'passwordRequired'),
})

type CredentialsForm = z.infer<typeof credentialsSchema>

/* Entrance choreography: the aurora is already there, then the logo, card and
 * footnote arrive as one stagger. Children only translate and fade — the 3D
 * rotation axis belongs exclusively to the pointer tilt, so entrance and tilt
 * never fight over the same transform. */
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
}

const rise = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.slow, ease: ease.out },
  },
}

/** The three background orbs: position, size, tint and a per-orb drift loop.
 *  Durations are mutually prime-ish so the composition never visibly repeats. */
const ORBS: Array<{
  className: string
  drift: { x: number[]; y: number[]; scale: number[] }
  seconds: number
}> = [
  {
    className: 'top-[-14rem] left-[8%] size-[34rem] bg-blue-200/50',
    drift: { x: [0, 50, -30, 0], y: [0, 30, 60, 0], scale: [1, 1.12, 0.96, 1] },
    seconds: 26,
  },
  {
    className: 'right-[4%] bottom-[-12rem] size-[30rem] bg-brand/15',
    drift: { x: [0, -60, 20, 0], y: [0, -40, -10, 0], scale: [1, 0.9, 1.08, 1] },
    seconds: 32,
  },
  {
    className: 'top-[30%] right-[28%] size-[22rem] bg-blue-300/30',
    drift: { x: [0, 40, -50, 0], y: [0, -50, 30, 0], scale: [1, 1.15, 0.92, 1] },
    seconds: 38,
  },
]

/* Sign-in is the one screen with a single job and no density budget, so its
 * controls run larger than the app's 36px operational default. The overrides are
 * LOCAL on purpose: CONTROL_HEIGHT and the Input type size are shared by every
 * dense sheet in the product, and growing them there to flatter this page would
 * cost rows everywhere else. `[&>input]` reaches the inner field because Input
 * styles the shell and the input separately. */
const FIELD_SCALE = '[&>label]:text-[13.5px] [&>p]:text-[13px]'
const CONTROL_SCALE = 'h-12 text-[15px] [&>input]:text-[15px]'

export function LoginPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const signIn = useSignIn()

  // Binds the pre-session language choice to i18next and `<html lang>`. The
  // session's own preference takes over once it resolves (FR-58).
  const language = useLanguage()

  const { data: user, isPending: sessionPending } = useSession()

  const [showPassword, setShowPassword] = useState(false)
  const errorRef = useRef<HTMLDivElement>(null)
  const errorId = useId()

  /* Motion is a courtesy, not a requirement: under prefers-reduced-motion the
   * tilt, the sheen and the orb drift all switch off, and the entrance stagger
   * collapses to its final frame. The CSS kill-switch in index.css cannot reach
   * Framer's rAF-driven inline styles, so this hook is the real gate. */
  const reduceMotion = useReducedMotion()

  /* Pointer position over the card, 0..1 on each axis, springed so the tilt
   * glides rather than jitters. 0.5/0.5 is "pointer nowhere": flat. */
  const pointerX = useMotionValue(0.5)
  const pointerY = useMotionValue(0.5)
  const springCfg = { stiffness: 160, damping: 20, mass: 0.6 }
  const rotateX = useSpring(useTransform(pointerY, [0, 1], [7, -7]), springCfg)
  const rotateY = useSpring(useTransform(pointerX, [0, 1], [-9, 9]), springCfg)

  /* A brand-tinted sheen that follows the pointer across the card face —
   * the "light source" the tilt is implying. Percentages, not px, so it needs
   * no knowledge of the card's rendered size. */
  const sheenX = useTransform(pointerX, (v) => `${v * 100}%`)
  const sheenY = useTransform(pointerY, (v) => `${v * 100}%`)
  const sheen = useMotionTemplate`radial-gradient(20rem circle at ${sheenX} ${sheenY}, rgba(57, 57, 255, 0.07), transparent 70%)`

  const onCardPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (reduceMotion) return
    const rect = event.currentTarget.getBoundingClientRect()
    pointerX.set((event.clientX - rect.left) / rect.width)
    pointerY.set((event.clientY - rect.top) / rect.height)
  }

  const onCardPointerLeave = () => {
    pointerX.set(0.5)
    pointerY.set(0.5)
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CredentialsForm>({
    resolver: zodResolver(credentialsSchema),
    // Validate on submit, then live once a field has been corrected. Validating
    // an email on every keystroke marks it invalid while it is still being
    // typed, which is a scolding rather than a help.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { email: '', password: '' },
  })

  /* The reason the user is here, when they did not come on purpose. The guard
   * passes `expired` for a session that ran out its idle or absolute clock, so
   * the interruption is explained once rather than looking like a bug. */
  const expired = Boolean((location.state as { expired?: boolean } | null)?.expired)

  const failure = signIn.isError ? signInFailure(signIn.error) : null

  /* Move the reader to the refusal. A submit that leaves focus in the password
   * box and prints a message above the fold is silent to a screen reader and
   * easy to miss on a phone. `role="alert"` announces it; focusing carries the
   * eye and the keyboard there too. */
  useEffect(() => {
    if (failure) errorRef.current?.focus()
  }, [failure])

  // Already signed in — never show a sign-in form to someone who has a session.
  if (user) return <Navigate to={DEFAULT_LANDING} replace />

  const onSubmit = handleSubmit((credentials) => {
    signIn.mutate(credentials, {
      onSuccess: () => {
        /* Back to whatever they were trying to reach, or home. `replace` keeps
         * the sign-in page out of history, so Back from the dashboard does not
         * land on a form the user has already satisfied. */
        const from = (location.state as { from?: string } | null)?.from
        navigate(from ?? DEFAULT_LANDING, { replace: true })
      },
    })
  })

  const message = failure
    ? {
        invalid: {
          title: t('auth.signIn.failed'),
          detail: t('auth.signIn.failedDetail'),
        },
        throttled: {
          title: t('auth.signIn.throttled'),
          detail: t('auth.signIn.throttledDetail'),
        },
        server: {
          title: t('errors.server.title'),
          detail: t('errors.server.detail'),
        },
      }[failure]
    : expired
      ? { title: t('auth.signIn.expired'), detail: t('auth.signIn.expiredDetail') }
      : null

  return (
    <div className="bg-surface-page relative flex min-h-dvh flex-col items-center justify-center overflow-hidden px-4 py-10">
      {/* ═══ Decorative layer. aria-hidden, never intercepts a pointer. ═══ */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* The original brand wash, kept as the base coat under the orbs. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(60rem 32rem at 50% -12%, var(--color-brand-soft), transparent 70%)',
          }}
        />

        {/* Aurora: three heavily blurred brand-tint orbs on slow, unequal
            drift loops. Transform-only animation, so it stays on the
            compositor and costs no layout work. */}
        {ORBS.map((orb, index) => (
          <motion.div
            key={index}
            className={`absolute rounded-full blur-3xl ${orb.className}`}
            animate={reduceMotion ? undefined : orb.drift}
            transition={{
              duration: orb.seconds,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* A faint dot grid, masked so it only reads around the centre of the
            screen — texture for the glass card to sit on. */}
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              'radial-gradient(circle, var(--color-neutral-300) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(38rem 26rem at 50% 45%, black, transparent 75%)',
            WebkitMaskImage:
              'radial-gradient(38rem 26rem at 50% 45%, black, transparent 75%)',
          }}
        />
      </div>

      {/* The switch is here because language is a per-user setting on the user
          record (FR-58, AD-27) and there is no user yet. Pre-session it writes
          to the same localStorage key the i18next detector reads, so the choice
          survives the reload and the sign-in itself; the session's own
          preference takes over the moment it arrives. */}
      <div className="absolute top-4 right-4 z-10 sm:top-6 sm:right-6">
        <LanguageSelect
          value={language}
          onChange={(next: Language) => {
            window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next)
            void i18n.changeLanguage(next)
          }}
        />
      </div>

      <motion.main
        variants={stagger}
        initial={reduceMotion ? false : 'hidden'}
        animate="visible"
        className="relative z-10 flex w-full max-w-[440px] flex-col items-center gap-8"
        // The perspective lives on the parent so the card rotates in a real
        // camera space instead of skewing in its own plane.
        style={{ perspective: 1100 }}
      >
        {/* Brand artwork is never redrawn or recoloured; the blue mark is the
            colourway for a light surface. The mark alone rather than a lockup:
            the wordmark would repeat the product name the card already carries,
            and it is drawn taller than the lockup was so the brand still opens
            the screen. */}
        <motion.div variants={rise}>
          <Logo variant="mark-blue" height={48} />
        </motion.div>

        <motion.div variants={rise} className="w-full">
          <motion.div
            onPointerMove={onCardPointerMove}
            onPointerLeave={onCardPointerLeave}
            style={{
              rotateX: reduceMotion ? 0 : rotateX,
              rotateY: reduceMotion ? 0 : rotateY,
              transformStyle: 'preserve-3d',
            }}
            className="border-line relative w-full rounded-[var(--radius-DEFAULT)] border bg-white/75 p-8 shadow-[0_24px_48px_-16px_rgba(31,31,51,0.18),var(--shadow-raised)] backdrop-blur-xl"
          >
            {/* Pointer-following sheen — the light the tilt implies. Painted
                above the card face but below the controls, and inert. */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-[inherit]"
              style={{ background: sheen }}
            />

            {/* Everything interactive sits on a raised 3D layer, so tilting
                the card visibly separates content from surface. */}
            <div style={{ transform: 'translateZ(28px)' }}>
              <div className="mb-7 flex flex-col gap-1.5">
                <h1 className="text-text-heading text-[22px] leading-tight font-medium">
                  {t('auth.signIn.title')}
                </h1>
                <p className="text-text-muted text-[14px] leading-snug font-light">
                  {t('auth.signIn.subtitle')}
                </p>
              </div>

              {/* The banner animates open rather than popping: a refusal that
                  physically pushes the form down is harder to miss than one
                  that appears in place. Exit is quicker — dismissals never
                  make the user wait. */}
              <AnimatePresence initial={false}>
                {message ? (
                  <motion.div
                    key={failure ?? 'expired'}
                    initial={{ opacity: 0, height: 0, y: -6 }}
                    animate={{
                      opacity: 1,
                      height: 'auto',
                      y: 0,
                      transition: { duration: duration.base, ease: ease.out },
                    }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: { duration: duration.fast, ease: ease.in },
                    }}
                    className="overflow-hidden"
                  >
                    <div
                      ref={errorRef}
                      id={errorId}
                      // -1 so it can take focus programmatically without
                      // joining the tab order, where an alert would be an odd
                      // thing to land on.
                      tabIndex={-1}
                      role="alert"
                      className={`mb-5 rounded-md border px-4 py-3 outline-none ${
                        expired && !failure
                          ? 'bg-brand-soft border-brand-border'
                          : 'bg-negative-soft border-negative/25'
                      }`}
                    >
                      <p
                        className={`text-[13.5px] leading-snug font-medium ${
                          expired && !failure ? 'text-brand' : 'text-negative'
                        }`}
                      >
                        {message.title}
                      </p>
                      <p className="text-text-body mt-1 text-[13px] leading-snug font-light">
                        {message.detail}
                      </p>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
                <Field
                  className={FIELD_SCALE}
                  label={t('auth.signIn.email')}
                  error={
                    errors.email
                      ? t(`auth.validation.${errors.email.message as 'emailRequired'}`)
                      : undefined
                  }
                >
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Input
                      {...register('email')}
                      id={id}
                      aria-describedby={describedBy}
                      aria-invalid={Boolean(errors.email)}
                      type="email"
                      inputMode="email"
                      autoComplete="username"
                      // The only field on the page worth opening the keyboard for.
                      autoFocus
                      spellCheck={false}
                      placeholder={t('auth.signIn.emailPlaceholder')}
                      iconLeft={Mail}
                      disabled={signIn.isPending}
                      className={CONTROL_SCALE}
                    />
                  )}
                </Field>

                <Field
                  className={FIELD_SCALE}
                  label={t('auth.signIn.password')}
                  error={
                    errors.password
                      ? t(
                          `auth.validation.${errors.password.message as 'passwordRequired'}`,
                        )
                      : undefined
                  }
                >
                  {({ id, 'aria-describedby': describedBy }) => (
                    <div className="relative">
                      <Input
                        {...register('password')}
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={Boolean(errors.password)}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        iconLeft={Lock}
                        disabled={signIn.isPending}
                        className={`${CONTROL_SCALE} [&>input]:pr-11`}
                      />
                      {/* Reveal is a real accessibility affordance on a phone,
                          where a mistyped password is otherwise invisible and
                          the only recourse is to clear the box and start
                          again. */}
                      <button
                        type="button"
                        onClick={() => setShowPassword((shown) => !shown)}
                        aria-label={t(
                          showPassword
                            ? 'auth.signIn.hidePassword'
                            : 'auth.signIn.showPassword',
                        )}
                        aria-pressed={showPassword}
                        className="text-text-faint hover:text-text-body absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center rounded-r-md transition-colors duration-[var(--dur-fast)]"
                      >
                        {showPassword ? (
                          <EyeOff size={17} aria-hidden />
                        ) : (
                          <Eye size={17} aria-hidden />
                        )}
                      </button>
                    </div>
                  )}
                </Field>

                {/* whileTap on a wrapper, not the Button: the shared Button owns
                    colour and focus behaviour, and a submit that visibly gives
                    under the finger is worth one extra div. */}
                <motion.div
                  whileHover={reduceMotion ? undefined : { scale: 1.015 }}
                  whileTap={reduceMotion ? undefined : { scale: 0.985 }}
                  transition={{ duration: duration.fast, ease: ease.out }}
                  className="mt-1"
                >
                  <Button
                    type="submit"
                    size="lg"
                    // Disabled while the session query is still in flight too:
                    // submitting before we know whether a session already exists
                    // would sign in over the top of one.
                    disabled={signIn.isPending || sessionPending}
                    aria-busy={signIn.isPending}
                    // Pinned to the 48px of the fields above it rather than
                    // left at `lg`'s 40px — a submit shorter than the boxes it
                    // completes reads as a different control set.
                    className="h-12 w-full text-[15px] shadow-[0_10px_24px_-10px_rgba(57,57,255,0.45)]"
                  >
                    {signIn.isPending ? t('auth.signIn.pending') : t('auth.signIn.submit')}
                  </Button>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </motion.div>

        <motion.p
          variants={rise}
          className="text-text-muted max-w-[44ch] text-center text-[13px] leading-snug font-light"
        >
          {t('auth.signIn.forgotten')}
        </motion.p>
      </motion.main>
    </div>
  )
}
